import { ClassificationEnum } from '@/types/sensor/classification.types';
import { EditableSensorOrSubSensorWithShorthand, EditableType } from '@/types/sensor/sensorInstallation.types';
import {
  AdsbSensorConfiguration,
  AeroscopeSensorConfiguration,
  AlertTimeoutThreshold,
  ArdronisSensorConfiguration,
  AutomatonSensorConfiguration,
  AvisoSensorConfiguration,
  BlastSensorConfiguration,
  CommonConfiguration,
  Dfb6SensorConfiguration,
  FlirSensorConfiguration,
  GenericSensorConfiguration,
  GroundStationSensorConfiguration,
  HolodroneSensorConfiguration,
  HolopticsSensorConfiguration,
  HostConfiguration,
  MajesSensorConfiguration,
  MavlinkReferentialEnum,
  PortConfiguration,
  RemoteHostConfiguration,
  RobinMaxSensorConfiguration,
  SenidSensorConfiguration,
  SensorTypeEnum,
  SkyEyeSensorConfiguration,
  SkyJackerSensorConfiguration,
  SpexerSensorConfiguration,
  SpexerVersionEnum,
  XentaSensorConfiguration,
  ZmerSensorConfiguration,
} from '@/types/sensor/sensors';

export const DEFAULT_HOST_CONFIGURATION: HostConfiguration = {
  host: '',
};

export const DEFAULT_HOST_PORT_CONFIGURATION: PortConfiguration & HostConfiguration = {
  host: '',
  port: 0,
};

export const DEFAULT_PORT_CONFIGURATION: PortConfiguration = {
  port: 0,
};

export const DEFAULT_REMOTE_HOST_CONFIGURATION: RemoteHostConfiguration = {
  remoteHost: null,
  remotePort: null,
};

export const DEFAULT_ALERT_TIMEOUT_THRESHOLD: AlertTimeoutThreshold = {
  warningNonDetectionTimeoutThreshold: null,
  nonDetectionTimeoutThreshold: null,
};

export const DEFAULT_USEFUL_CONFIGURATION: Partial<CommonConfiguration> = {
  appCode: '',
  site: '',
  code: '',
  name: '',
  active: false,
  maintenance: false,
  saveRawData: false,
  sensorPosition: { longitude: 2.36, latitude: 48.7, type: '2D' },
  sensorAltitude: 0,
  sensorColor: null,
  phoneNumber: null,
  defaultTargetClassification: ClassificationEnum.UNKNOWN,
  timeoutThreshold: 10000,
  trackLostTime: 120000,
  trackDeleteTime: 300000,
  marks: [],
};

export const DEFAULT_STALE_CONFIGURATION: Partial<CommonConfiguration> = {
  airportZone: null,
  stateOwnedZone: null,
  sector: null,
  infrastructure: null,
  horizontalPrecision: 50,
  verticalPrecision: 50,
};

export const EMPTY_ADSB = {
  ...DEFAULT_USEFUL_CONFIGURATION,
  ...DEFAULT_HOST_PORT_CONFIGURATION,
  ...DEFAULT_ALERT_TIMEOUT_THRESHOLD,
  filterAreaMaxAltitude: 1000,
  filterAreaRadius: 100000,
  filterMaxSpeed: 0, // Currently useless
  timeZone: 'Europe/Paris',
  ...DEFAULT_STALE_CONFIGURATION,
  type: SensorTypeEnum.ADSB,
} as AdsbSensorConfiguration;

export const EMPTY_AEROSCOPE = {
  ...DEFAULT_USEFUL_CONFIGURATION,
  ...DEFAULT_HOST_PORT_CONFIGURATION,
  ...DEFAULT_ALERT_TIMEOUT_THRESHOLD,
  port: 80,
  controlUrl: '',
  username: '',
  password: '',
  dataRefresh: 1000,
  tokenRefresh: 600000,
  timeZone: 'Europe/Paris',
  useHttps: false,
  ...DEFAULT_STALE_CONFIGURATION,
  type: SensorTypeEnum.AEROSCOPE,
} as AeroscopeSensorConfiguration;

export const EMPTY_ARDRONIS = {
  ...DEFAULT_USEFUL_CONFIGURATION,
  ...DEFAULT_HOST_PORT_CONFIGURATION,
  ...DEFAULT_ALERT_TIMEOUT_THRESHOLD,
  commandPort: 0,
  eventChannel: '',
  eventPort: 0,
  antennas: [],
  ...DEFAULT_STALE_CONFIGURATION,
  type: SensorTypeEnum.ARDRONIS,
} as ArdronisSensorConfiguration;

export const EMPTY_HOLODRONE = {
  ...DEFAULT_USEFUL_CONFIGURATION,
  ...DEFAULT_HOST_PORT_CONFIGURATION,
  ...DEFAULT_ALERT_TIMEOUT_THRESHOLD,
  port: 5679,
  holodroneId: 0,
  apiKey: '',
  grpcMode: false,
  timeOverload: false,
  ...DEFAULT_STALE_CONFIGURATION,
  type: SensorTypeEnum.HOLODRONE,
} as HolodroneSensorConfiguration;

export const EMPTY_AVISO = {
  ...DEFAULT_USEFUL_CONFIGURATION,
  ...DEFAULT_HOST_PORT_CONFIGURATION,
  ...DEFAULT_ALERT_TIMEOUT_THRESHOLD,
  filterAreaMaxAltitude: 1000,
  filterAreaRadius: 100000,
  filterMaxSpeed: 0, // Currently useless
  sac: 0,
  sic: 0,
  originSac: null,
  originSic: null,
  xoffset: 0,
  yoffset: 0,
  ...DEFAULT_STALE_CONFIGURATION,
  type: SensorTypeEnum.AVISO,
} as AvisoSensorConfiguration;

export const EMPTY_BLAST = {
  ...DEFAULT_USEFUL_CONFIGURATION,
  ...DEFAULT_HOST_PORT_CONFIGURATION,
  mc2StatusTimer: 250,
  currentConfig: null,
  lastCalibration: '',
  lastCheck: '',
  configs: [],
  modules: [],
  modes: [],
  ...DEFAULT_STALE_CONFIGURATION,
  type: SensorTypeEnum.BLAST,
} as BlastSensorConfiguration;

export const EMPTY_DFB6 = {
  ...DEFAULT_USEFUL_CONFIGURATION,
  ...DEFAULT_HOST_PORT_CONFIGURATION,
  mc2StatusTimer: 250,
  currentConfig: '',
  lastCalibration: '',
  configs: [],
  modules: [],
  modes: [],
  ...DEFAULT_STALE_CONFIGURATION,
  type: SensorTypeEnum.DFB6,
} as Dfb6SensorConfiguration;

export const EMPTY_GENERIC: GenericSensorConfiguration = {
  ...DEFAULT_USEFUL_CONFIGURATION,
  ...DEFAULT_HOST_PORT_CONFIGURATION,
  ...DEFAULT_ALERT_TIMEOUT_THRESHOLD,
  timeOverload: true,
  ...DEFAULT_STALE_CONFIGURATION,
  type: SensorTypeEnum.GENERIC,
  sources: [],
  detectionAreaFilter: null,
  subSensors: [],
} as GenericSensorConfiguration;

export const EMPTY_GROUND_STATION = {
  ...DEFAULT_USEFUL_CONFIGURATION,
  ...DEFAULT_REMOTE_HOST_CONFIGURATION,
  ...DEFAULT_PORT_CONFIGURATION,
  ...DEFAULT_ALERT_TIMEOUT_THRESHOLD,
  remoteHost: null,
  remotePort: null,
  manualRemoteConfiguration: false,
  referential: MavlinkReferentialEnum.LAT_LNG_ALT_GROUND,
  drones: [
    {
      id: null,
      code: '',
      name: '',
      systemId: 1,
      canControl: true,
      useRawPosition: false,
      modules: [],
    },
  ],
  ...DEFAULT_STALE_CONFIGURATION,
  type: SensorTypeEnum.GROUND_STATION,
} as GroundStationSensorConfiguration;

export const EMPTY_AUTOMATON = {
  ...DEFAULT_USEFUL_CONFIGURATION,
  ...DEFAULT_HOST_PORT_CONFIGURATION,
  username: '',
  password: '',
  cabinets: [],
  ...DEFAULT_STALE_CONFIGURATION,
  type: SensorTypeEnum.AUTOMATON,
} as AutomatonSensorConfiguration;

export const EMPTY_FLIR = {
  ...DEFAULT_USEFUL_CONFIGURATION,
  ...DEFAULT_HOST_PORT_CONFIGURATION,
  ...DEFAULT_STALE_CONFIGURATION,
  ...DEFAULT_ALERT_TIMEOUT_THRESHOLD,
  type: SensorTypeEnum.FLIR,
} as FlirSensorConfiguration;

export const EMPTY_HOLOPTICS = {
  ...DEFAULT_USEFUL_CONFIGURATION,
  ...DEFAULT_HOST_PORT_CONFIGURATION,
  apiKey: '',
  tiltOffset: null,
  vxMax: 0,
  vxMin: 0,
  vyMax: 0,
  vyMin: 0,
  panOffset: 0,
  range: 0,
  subCameras: [],
  ...DEFAULT_STALE_CONFIGURATION,
  type: SensorTypeEnum.AF2206,
} as HolopticsSensorConfiguration;

export const EMPTY_AXIS = {
  ...DEFAULT_USEFUL_CONFIGURATION,
  ...DEFAULT_HOST_PORT_CONFIGURATION,
  apiKey: '',
  tiltOffset: null,
  vxMax: 0,
  vxMin: 0,
  vyMax: 0,
  vyMin: 0,
  panOffset: 0,
  range: 0,
  subCameras: [],
  ...DEFAULT_STALE_CONFIGURATION,
  type: SensorTypeEnum.AXIS,
} as HolopticsSensorConfiguration;

export const EMPTY_MAJES = {
  ...DEFAULT_USEFUL_CONFIGURATION,
  ...DEFAULT_HOST_PORT_CONFIGURATION,
  mc2StatusTimer: 250,
  mc2Id: 0,
  modules: [],
  sectors: [],
  ...DEFAULT_STALE_CONFIGURATION,
  type: SensorTypeEnum.MAJES,
} as MajesSensorConfiguration;

export const EMPTY_ROBIN_MAX = {
  ...DEFAULT_USEFUL_CONFIGURATION,
  ...DEFAULT_HOST_PORT_CONFIGURATION,
  ...DEFAULT_ALERT_TIMEOUT_THRESHOLD,
  radars: [],
  ...DEFAULT_STALE_CONFIGURATION,
  type: SensorTypeEnum.ROBIN_MAX,
} as RobinMaxSensorConfiguration;

export const EMPTY_SENID = {
  ...DEFAULT_USEFUL_CONFIGURATION,
  ...DEFAULT_HOST_PORT_CONFIGURATION,
  ...DEFAULT_ALERT_TIMEOUT_THRESHOLD,
  username: null,
  password: '',
  vhost: '',
  antennas: [],
  ...DEFAULT_STALE_CONFIGURATION,
  type: SensorTypeEnum.SENID,
} as SenidSensorConfiguration;

export const EMPTY_SKY_EYE = {
  ...DEFAULT_USEFUL_CONFIGURATION,
  ...DEFAULT_HOST_PORT_CONFIGURATION,
  ...DEFAULT_ALERT_TIMEOUT_THRESHOLD,
  username: '',
  password: '',
  dataRefresh: 1000,
  timeZone: 'Europe/Paris',
  antennas: [],
  ...DEFAULT_STALE_CONFIGURATION,
  type: SensorTypeEnum.SKY_EYE,
} as SkyEyeSensorConfiguration;

export const EMPTY_SKY_JACKER = {
  ...DEFAULT_USEFUL_CONFIGURATION,
  ...DEFAULT_HOST_PORT_CONFIGURATION,
  antennas: [],
  ...DEFAULT_STALE_CONFIGURATION,
  type: SensorTypeEnum.SKY_JACKER,
} as SkyJackerSensorConfiguration;

export const EMPTY_XENTA = {
  ...DEFAULT_USEFUL_CONFIGURATION,
  ...DEFAULT_REMOTE_HOST_CONFIGURATION,
  ...DEFAULT_PORT_CONFIGURATION,
  ...DEFAULT_ALERT_TIMEOUT_THRESHOLD,
  aperture: 0,
  blindRadius: 0,
  endDetectionRadius: 0,
  intermediateRadius: 0,
  remoteHost: null,
  remotePort: null,
  sac: 0,
  sic: 0,
  originSac: null,
  originSic: null,
  elevationMinAperture: null,
  elevationMaxAperture: null,
  ...DEFAULT_STALE_CONFIGURATION,
  type: SensorTypeEnum.XENTA,
} as XentaSensorConfiguration;

export const EMPTY_SPEXER = {
  ...DEFAULT_USEFUL_CONFIGURATION,
  ...DEFAULT_HOST_PORT_CONFIGURATION,
  ...DEFAULT_ALERT_TIMEOUT_THRESHOLD,
  radars: [],
  version: SpexerVersionEnum.SPEXER_500,
  ...DEFAULT_STALE_CONFIGURATION,
  type: SensorTypeEnum.SPEXER,
} as SpexerSensorConfiguration;

export const EMPTY_ZMER = {
  ...DEFAULT_USEFUL_CONFIGURATION,
  ...DEFAULT_REMOTE_HOST_CONFIGURATION,
  ...DEFAULT_PORT_CONFIGURATION,
  commandHost: null,
  commandPort: null,
  charmUsername: '',
  charmPassword: '',
  remoteHost: null,
  remotePort: null,
  vxMin: 0,
  vxMax: 0,
  vyMin: 0,
  vyMax: 0,
  zoomSynchronization: null,
  subCameras: [],
  ...DEFAULT_STALE_CONFIGURATION,
  type: SensorTypeEnum.ZMER,
} as ZmerSensorConfiguration;

export const emptySensorByType: Record<EditableType, EditableSensorOrSubSensorWithShorthand> = {
  [SensorTypeEnum.ADSB]: EMPTY_ADSB,
  [SensorTypeEnum.AEROSCOPE]: EMPTY_AEROSCOPE,
  [SensorTypeEnum.ARDRONIS]: EMPTY_ARDRONIS,
  [SensorTypeEnum.AUTOMATON]: EMPTY_AUTOMATON,
  [SensorTypeEnum.AVISO]: EMPTY_AVISO,
  [SensorTypeEnum.BLAST]: EMPTY_BLAST,
  [SensorTypeEnum.DFB6]: EMPTY_DFB6,
  [SensorTypeEnum.FLIR]: EMPTY_FLIR,
  [SensorTypeEnum.GROUND_STATION]: EMPTY_GROUND_STATION,
  [SensorTypeEnum.GENERIC]: EMPTY_GENERIC,
  [SensorTypeEnum.HOLODRONE]: EMPTY_HOLODRONE,
  [SensorTypeEnum.AF2206]: EMPTY_HOLOPTICS,
  [SensorTypeEnum.AXIS]: EMPTY_AXIS,
  [SensorTypeEnum.MAJES]: EMPTY_MAJES,
  [SensorTypeEnum.ROBIN_MAX]: EMPTY_ROBIN_MAX,
  [SensorTypeEnum.SENID]: EMPTY_SENID,
  [SensorTypeEnum.SKY_EYE]: EMPTY_SKY_EYE,
  [SensorTypeEnum.SKY_JACKER]: EMPTY_SKY_JACKER,
  [SensorTypeEnum.SPEXER]: EMPTY_SPEXER,
  [SensorTypeEnum.XENTA]: EMPTY_XENTA,
  [SensorTypeEnum.ZMER]: EMPTY_ZMER,
};
